import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { reportClientError } from '@/lib/observability'
import { isStaleChunkError, StaleBuildError } from '@/lib/staleBuild'

// ─── Route-level code splitting that survives a deploy ──────────────────────────
// A plain `lazy()` turns a routine deploy into "Something went wrong" for anybody
// who already had the app open: the chunk it wants was replaced, the SPA rewrite
// answers with index.html, the browser refuses to run HTML as a module, and
// React.lazy rethrows the rejection during render.
//
// The chunk is genuinely gone, so there is nothing to retry. What this adds is a
// NAME for that failure, so the ErrorBoundary can say what actually happened
// instead of showing a generic crash. Why the recovery is a prompt rather than an
// automatic reload is explained in lib/staleBuild.ts.
//
// Two related pieces:
//   - vercel.json excludes /assets/ from the SPA rewrite, so a stale chunk 404s
//     as itself instead of masquerading as an HTML page;
//   - the ErrorBoundary resets on navigation, so a screen that fails cannot wedge
//     every subsequent menu click — which is also what lets somebody step back to
//     a working screen and save their work before reloading.

/**
 * `React.lazy` for a route, with a stale build reported as itself.
 *
 * Use this for every route-level import. A module that throws at import time is
 * rethrown untouched — only a failure to LOAD the chunk is a stale build.
 */
export function lazyRoute<P extends object>(
  load: () => Promise<{ default: ComponentType<P> }>,
): LazyExoticComponent<ComponentType<P>> {
  return lazy(() =>
    load().catch((err: unknown) => {
      if (!isStaleChunkError(err)) throw err

      // Self-healing from the person's point of view, but worth knowing about: a
      // steady stream of these says people are being interrupted by every deploy.
      reportClientError({
        level: 'warn',
        source: 'stale-chunk',
        message: 'lazy route chunk missing — the running build has been replaced',
        error: err instanceof Error ? err : undefined,
      })

      throw new StaleBuildError(err)
    }),
  )
}
